import React from 'react';
import { Spring, Transition, animated, config } from 'react-spring';
import { darken } from 'polished';

import { useParent } from '../context';
import { useHover } from '../../hooks';
import { mainButtonSize } from '../consts';
import ChatIcon from './icons/ChatIcon';

const badgeInStyles = { opacity: 1, transform: 'scale(1)' };
const badgeOutStyles = { opacity: 0, transform: 'scale(0)' };

/** Unread badge */
const Badge = ({ count }: { count: number }) => {
  const size = count < 10 ? 21 : count < 100 ? 25 : 29;
  return (
    <Transition
      native
      config={config.stiff}
      items={count > 0}
      from={badgeOutStyles}
      enter={badgeInStyles}
      leave={badgeOutStyles}
    >
      {showBadge =>
        showBadge &&
        (badgeStyle => (
          <animated.div
            style={badgeStyle}
            css={{
              position: 'absolute',
              top: -4,
              right: -4,
              width: size,
              height: size,
              lineHeight: 0,
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: '#d0021b',
              color: 'white',
              fontSize: 12,
              fontWeight: 700,
              transformOrigin: 'center center',
            }}
          >
            {count}
          </animated.div>
        ))
      }
    </Transition>
  );
};

const inStyles = {
  opacity: 1,
  transform: 'translate(0%, 0%)',
};
const getOutStyles = (isMobile: boolean) => ({
  opacity: 0,
  transform: `translate(${isMobile ? '0%, 30%' : '30%, 0%'})`,
});

type Props = {
  isOpen: boolean;
  onClick: () => void;
  unseenCount: number;
};
export default function ChatButton({ isOpen, onClick, unseenCount }: Props) {
  const [hoverProps, isHovered] = useHover();
  const { isMobile } = useParent();
  return (
    <Transition
      native
      items={isOpen}
      update={{ isMobile }}
      from={getOutStyles(isMobile)}
      enter={inStyles}
      leave={getOutStyles(isMobile)}
    >
      {showButton =>
        !showButton &&
        (buttonStyle => (
          <animated.button
            style={buttonStyle}
            onClick={onClick}
            {...hoverProps}
            css={(theme: Theme) => ({
              position: 'absolute',
              bottom: theme.widget.closedSpace,
              right: theme.widget.closedSpace,
              width: mainButtonSize,
              height: mainButtonSize,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              border: 'none',
              padding: '7px 0 0 0',
              backgroundColor: theme.colors.primary,
              borderRadius: '50%',
              boxShadow: theme.shadows.widget,
              overflow: 'visible',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              ':hover': {
                backgroundColor: darken(0.08, theme.colors.primary),
              },
            })}
          >
            <Spring
              native
              to={{
                transformOrigin: 'center center',
                transform: `scale(${isHovered ? 1.2 : 1})`,
              }}
            >
              {iconStyle => (
                <animated.span style={iconStyle}>
                  <ChatIcon />
                </animated.span>
              )}
            </Spring>
            <Badge count={unseenCount} />
          </animated.button>
        ))
      }
    </Transition>
  );
}
